import React from "react";
import { Card, CardLabel } from "./ui/Card";
import { Num, Pct } from "./ui/Num";

/**
 * The four-cell headline strip at the top of the Broadcasts list.
 *
 * ---------------------------------------------------------------------------
 * THE ONE RULE THAT MATTERS MOST ON THIS SCREEN
 * ---------------------------------------------------------------------------
 * `uniquePush` and `uniqueEmail` are shown SIDE BY SIDE and are NEVER ADDED.
 *
 * `uniqueReached` is the server's own de-duplicated figure across both
 * channels. A user who is in the push audience and the email audience is two
 * sends and one person. Summing the two channel figures would claim we reached
 * more people than exist — inflating the headline number of the entire
 * console, on the screen executives read first.
 *
 * There is no `+` anywhere in this file, and there must never be one. If a
 * combined figure is ever wanted, it comes from the server as its own field.
 * ---------------------------------------------------------------------------
 *
 * Purely presentational: `overview` is the already-decoded object, every value
 * arrives as a prop, and nothing here fetches.
 */

/** Grid of exactly four cells; two-up on narrow screens so it cannot overflow. */
const GRID = "grid grid-cols-2 md:grid-cols-4";

const CELL = "p-5";
const DIVIDER = "border-r border-bc-hairline";

const VALUE = "text-2xl";
const SUBLINE = "mt-1 text-xs text-bc-soft";

/** Skeleton bars. Pulsing, in the real cell frame, so nothing moves on load. */
const BAR = "animate-pulse bg-bc-hairline rounded";

/** Marks the four cells for tests and for the divider logic. */
const CELL_TESTID = "bc-headline-cell";

/**
 * One tile of the strip. Always four of these, loading or not, so the strip
 * has a fixed height and the page below it never jumps.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.label - Uppercase micro-label.
 * @param {React.ReactNode} props.children - Value node plus optional subline.
 * @param {boolean} props.isLast - Suppresses the right-hand hairline.
 * @returns {React.ReactElement}
 */
const Cell = ({ label, children, isLast }) => (
  <div
    data-testid={CELL_TESTID}
    className={`${CELL} ${isLast ? "" : DIVIDER}`}
  >
    {label ? <CardLabel>{label}</CardLabel> : null}
    <div className="mt-1.5">{children}</div>
  </div>
);

/**
 * The loading frame: the same four cells, with bars where the label, value and
 * subline will land.
 *
 * @param {Object} props
 * @param {boolean} props.isLast
 * @returns {React.ReactElement}
 */
const SkeletonCell = ({ isLast }) => (
  <div data-testid={CELL_TESTID} className={`${CELL} ${isLast ? "" : DIVIDER}`}>
    <div className={`${BAR} h-3 w-24`} />
    <div className={`${BAR} mt-2.5 h-7 w-20`} />
    <div className={`${BAR} mt-2 h-3 w-28`} />
  </div>
);

/**
 * The channel-scoped reach subline.
 *
 * With the "All" tab both figures are shown, separated by a middot — two facts
 * standing next to each other, not an equation. With a channel tab active the
 * strip re-scopes and only that channel's figure is shown.
 *
 * @param {Object} props
 * @param {string} [props.channel] - Active channel tab id: "all" | "push" | "email".
 * @param {number|null} [props.push] - Unique users reached by push.
 * @param {number|null} [props.email] - Unique users reached by email.
 * @returns {React.ReactElement}
 */
const ReachSubline = ({ channel, push, email }) => {
  if (channel === "push") {
    return (
      <span>
        push <Num value={push} className="text-xs" />
      </span>
    );
  }

  if (channel === "email") {
    return (
      <span>
        email <Num value={email} className="text-xs" />
      </span>
    );
  }

  // Side by side. Never summed — see the file header.
  return (
    <span>
      push <Num value={push} className="text-xs" />
      {" · "}
      email <Num value={email} className="text-xs" />
    </span>
  );
};

/**
 * Should the "N scheduled ahead" line appear at all?
 *
 * `null` means the server did not say, and `0` means there is nothing queued —
 * neither is worth a line of its own. Only a real, positive count earns one.
 *
 * @param {unknown} scheduledAhead
 * @returns {boolean}
 */
const showsScheduled = (scheduledAhead) =>
  typeof scheduledAhead === "number" &&
  Number.isFinite(scheduledAhead) &&
  scheduledAhead > 0;

/**
 * The headline strip.
 *
 * A missing `overview` renders a strip of em dashes rather than nothing: the
 * list hook tolerates the overview call failing while the list itself
 * succeeds, and a degraded strip is honest where a vanished one is confusing.
 *
 * @param {Object} props
 * @param {Object|null} [props.overview] - Decoded overview: `{ broadcastsSent, scheduledAhead, uniqueReached, uniquePush, uniqueEmail, uniqueOpeners, uniqueClickers, openRatePct, clickRatePct }`. Any field may be null.
 * @param {string} [props.channel='all'] - Active channel tab: "all" | "push" | "email". Re-scopes the reach subline.
 * @param {boolean} [props.loading=false] - Renders the four-cell skeleton frame.
 * @param {number} [props.days=30] - Window size, in days, interpolated into the first label.
 * @param {string} [props.className] - Extra classes on the strip wrapper, merged last.
 * @returns {React.ReactElement}
 */
export const BroadcastHeadline = ({
  overview,
  channel = "all",
  loading = false,
  days = 30,
  className = "",
}) => {
  const data = overview || {};

  return (
    // `aria-live="polite"` so a refresh is announced without stealing focus
    // from whatever the user was doing in the list below.
    <section aria-live="polite" className={className}>
      <Card>
        {loading ? (
          <div className={GRID}>
            {[0, 1, 2, 3].map((index) => (
              <SkeletonCell key={index} isLast={index === 3} />
            ))}
          </div>
        ) : (
          <div className={GRID}>
            <Cell label={`Sent · ${days}d`} isLast={false}>
              <Num value={data.broadcastsSent} className={VALUE} emphasis />
              {showsScheduled(data.scheduledAhead) ? (
                <div className={SUBLINE}>
                  <Num value={data.scheduledAhead} className="text-xs" />{" "}
                  scheduled ahead
                </div>
              ) : null}
            </Cell>

            <Cell label="Unique users reached" isLast={false}>
              <Num value={data.uniqueReached} className={VALUE} emphasis />
              <div className={SUBLINE}>
                <ReachSubline
                  channel={channel}
                  push={data.uniquePush}
                  email={data.uniqueEmail}
                />
              </div>
            </Cell>

            <Cell label="Unique openers" isLast={false}>
              <Num value={data.uniqueOpeners} className={VALUE} emphasis />
              <div className={SUBLINE}>
                <Pct value={data.openRatePct} className="text-xs" /> of reached
              </div>
            </Cell>

            <Cell label="Unique clickers" isLast>
              <Num value={data.uniqueClickers} className={VALUE} emphasis />
              <div className={SUBLINE}>
                <Pct value={data.clickRatePct} className="text-xs" /> of reached
              </div>
            </Cell>
          </div>
        )}
      </Card>
    </section>
  );
};

export default BroadcastHeadline;
